/**
 * BannerHotelList component
 * Displays a gallery of hotel list banners for desktop and mobile, with sorted images and optional description overlay.
 * Used to visually showcase hotel options in the hotel result page.
 */

import React from "react";
import {
  HotelListBannersQuery,
  ContentJapanDatasourceHotelListBannerImage,
} from "@/types/generated";
import { Gallery } from "../../Gallery";
import Image from "next/image";
import { filterSelectionData } from "@/lib/utils";

interface IBannerHotelListProps {
  bannerHotelList: HotelListBannersQuery;
}

const BannerHotelList: React.FC<IBannerHotelListProps> = ({
  bannerHotelList,
}) => {
  /**
   * Sorts and filters images for the banner based on selection and validity.
   */

  const getSortedImages = (
    bannerItem: HotelListBannersQuery["contentJapanDatasourcesHotelListBanners"][0],
  ) => {
    if (!bannerItem?.images) return [];

    const selectionList = bannerItem.picked_images;
    const validImages = bannerItem.images.filter(
      (img): img is NonNullable<ContentJapanDatasourceHotelListBannerImage> =>
        img !== null,
    );

    if (!selectionList || selectionList.length === 0) return [];

    return filterSelectionData(validImages, selectionList);
  };

  if (!bannerHotelList?.contentJapanDatasourcesHotelListBanners?.[0]) {
    return <></>;
  }
  const bannerItem = bannerHotelList.contentJapanDatasourcesHotelListBanners[0];
  /**
   * Filter desktop and mobile images for gallery display
   */
  const desktopImages = getSortedImages(bannerItem).filter(
    (item) => item.desktop?.cloudflare_url || item.desktop?.url,
  );
  const mobileImages = getSortedImages(bannerItem).filter(
    (item) => item.mobile?.cloudflare_url || item.mobile?.url,
  );

  return (
    <>
      {/* Desktop Gallery */}
      <div className="relative hidden h-full w-full md:block">
        <Gallery
          dotClassName="md:p-1.5"
          isOnlyPageNumber
          activeDotClassName="max-md:bg-[var(--text-base-primary)]"
          inactiveDotClassName="max-md:bg-gray-400"
          navigationClassName="md:hidden text-[var(--text-base-primary)]"
          paginationClassName="md:bottom-12 bottom-10"
          containerClassName="h-full w-full"
        >
          {desktopImages.map((image, index) => {
            const isFirstImage = index < 2;
            return (
              <div
                key={
                  image?.desktop?.cloudflare_url ||
                  image?.desktop?.url ||
                  crypto.randomUUID()
                }
                className="relative h-full min-h-[480px] w-full"
              >
                <Image
                  src={
                    image?.desktop?.cloudflare_url || image?.desktop?.url || ""
                  }
                  alt={image?.desktop?.alternativeText ?? ""}
                  aria-label={image?.desktop?.caption ?? ""}
                  fill
                  priority={isFirstImage}
                  className="object-cover"
                />
                <div className="absolute inset-0 z-2 bg-linear-to-t from-black/80 via-black/30 to-transparent"></div>
              </div>
            );
          })}
        </Gallery>
        {bannerItem?.description && (
          <div className="pointer-events-none absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center text-white!">
            <div className="text-2xl font-(--font-regular) md:text-[45px]">
              {bannerItem?.description}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Gallery */}
      <div className="relative block h-full w-full md:hidden">
        <Gallery
          dotClassName="md:p-1.5"
          inactiveDotClassName="bg-gray-400"
          paginationClassName="md:bottom-12 bottom-10"
          containerClassName="h-full w-full"
        >
          {mobileImages.map((image, index) => {
            const isFirstImage = index === 0;
            return (
              <div
                key={
                  image?.mobile?.cloudflare_url ||
                  image?.mobile?.url ||
                  crypto.randomUUID()
                }
                className="relative h-full min-h-[480px] w-full"
              >
                <Image
                  src={image?.mobile?.cloudflare_url || image?.mobile?.url || ""}
                  alt={image?.mobile?.alternativeText ?? ""}
                  aria-label={image?.mobile?.caption ?? ""}
                  fill
                  priority={isFirstImage}
                  className="object-cover"
                />
                <div className="absolute inset-0 z-2 bg-linear-to-t from-black/80 via-black/30 to-transparent"></div>
              </div>
            );
          })}
        </Gallery>
        {bannerItem?.description && (
          <div className="pointer-events-none absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center text-white!">
            <div className="text-2xl font-(--font-regular) md:text-[45px]">
              {bannerItem?.description}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export { BannerHotelList };
